<template>
  <div class="tables-mobile-container">
    <!-- Main Content -->

    <v-container fluid class="pa-0">
      <!-- Pull to Refresh Indicator -->
      <v-progress-linear
        v-if="pullToRefreshRefreshing"
        indeterminate
        color="primary"
        height="2"
      />

      <!-- Statistics Section -->
      <section class="stats-section">
        <div class="stats-scroll-container">
          <div class="stats-cards">
            <div
              v-for="stat in statisticsArray"
              :key="stat.key"
              class="stat-card"
              :class="{
                'stat-card-active':
                  (stat.key === 'total' &&
                    (!activeStatFilter || activeStatFilter === '')) ||
                  (stat.key !== 'total' && activeStatFilter === stat.key),
              }"
              @click="filterByStatus(stat.key === 'total' ? '' : stat.key)"
            >
              <div class="stat-card-content">
                <div class="stat-info">
                  <div
                    class="stat-label"
                    :style="{ color: getStatColor(stat.color) }"
                  >
                    {{ $t(stat.label) }}
                  </div>
                  <div class="stat-value">{{ statistics[stat.key] }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <v-divider class="my-2" />

      <!-- Search and Filter Section -->
      <section>
        <v-card flat class="search-card">
          <v-card-text class="pa-2">
            <!-- Search and Zone Filter -->
            <v-row align="center">
              <v-col cols="8">
                <v-text-field
                  :value="search"
                  :placeholder="$t('search_tables')"
                  clearable
                  outlined
                  dense
                  hide-details
                  @input="
                    search = $event;
                    fetchItem();
                  "
                >
                  <template v-slot:append>
                    <v-icon small color="primary">fal fa-search</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="4">
                <v-select
                  :value="zone_filter"
                  :items="translatedZoneList"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  @change="
                    zone_filter = $event;
                    fetchItem();
                  "
                >
                  <template v-slot:append>
                    <v-icon small color="primary">fal fa-map-marker-alt</v-icon>
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </section>

      <!-- Tables Grid Section -->
      <section class="tables-grid-section">
        <div class="tables-container" ref="tablesContainer">
          <!-- Tables Grid -->
          <div class="tables-grid">
            <div
              v-for="table in sortedTables"
              :key="table.id"
              class="table-card-wrapper"
            >
              <v-card
                class="table-card"
                :class="[
                  `table-card-${table.status}`,
                  {
                    'table-selected':
                      selectedTable && selectedTable.id === table.id,
                    'has-new-order': table.active_ticket?.is_new_order,
                  },
                ]"
                outlined
                @click="selectTable(table)"
                @contextmenu.prevent="showTableMenu(table, $event)"
              >
                <!-- New Order Indicator -->
                <div
                  v-if="table.active_ticket?.is_new_order"
                  class="new-order-indicator"
                />

                <v-card-text class="pa-2">
                  <!-- Table Header -->
                  <div class="table-header">
                    <div class="table-name">{{ table.name }}</div>
                    <v-chip
                      :color="tableStatusColor(table.status)"
                      text-color="white"
                      x-small
                      class="table-status"
                    >
                      {{ $t(table.status) }}
                    </v-chip>
                  </div>

                  <!-- Zone Info -->
                  <div
                    v-if="table.zone"
                    class="zone-info"
                    @click.stop="navigateToZones"
                    style="cursor: pointer; text-decoration: underline"
                  >
                    <v-icon x-small color="grey">fal fa-map-marker-alt</v-icon>
                    <span class="zone-name">{{ table.zone.name }}</span>
                  </div>

                  <!-- Order Info -->
                  <div class="order-info">
                    <div class="order-amount">
                      <span class="amount-value">
                        {{
                          formatNumber(table?.active_ticket?.total_price || 0)
                        }}
                      </span>
                    </div>
                  </div>

                  <!-- Quick Actions -->
                  <div class="quick-actions">
                    <v-btn
                      v-if="table.status === 'available'"
                      icon
                      x-small
                      @click.stop="bookTableFromCard(table)"
                      class="mr-1"
                    >
                      <v-icon size="14">fal fa-calendar-alt</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      @click.stop="generateQR(table)"
                      class="mr-1"
                    >
                      <v-icon size="14">fal fa-qrcode</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      @click.stop="toggleTableMenu(table, $event)"
                    >
                      <v-icon size="14">fal fa-ellipsis-v</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="items.data?.length === 0 && !loading" class="empty-state">
            <div class="empty-state-content">
              <v-icon size="48" color="grey lighten-1">
                fal fa-layer-group
              </v-icon>
              <div class="text-h6 mt-3">{{ $t("no_data") }}</div>
              <div class="text-body-2 grey--text mt-1">
                No tables match filters
              </div>
              <v-btn color="primary" class="mt-3" @click="clearFilters">
                Clear Filters
              </v-btn>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <v-progress-circular indeterminate color="primary" size="32" />
            <div class="text-caption mt-2">{{ $t("loading_tables") }}</div>
          </div>

          <!-- Infinite Scroll Loading -->
          <div
            v-if="loading && items.data?.length > 0"
            class="infinite-scroll-loading"
          >
            <v-progress-circular indeterminate color="primary" size="24" />
          </div>
        </div>
      </section>
    </v-container>

    <!-- Floating Action Button with Menu -->
    <div class="fab-container">
      <v-menu
        v-model="showFabMenu"
        bottom
        left
        offset-y
        :close-on-content-click="true"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            color="primary"
            class="fab-main rounded-lg"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>fal fa-plus</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="newItem">
            <v-list-item-icon>
              <v-icon>fal fa-plus-circle</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ $t("new_data") }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Bottom Sheet for Table Details -->
    <v-bottom-sheet
      :value="showTableDetails"
      @input="showTableDetails = $event"
      :persistent="!!selectedTable?.active_ticket"
      max-width="100%"
      content-class="table-details-bottom-sheet"
    >
      <v-card v-if="selectedTable" class="table-details-card">
        <!-- Sheet Header -->
        <div class="sheet-header">
          <div class="sheet-title">
            <div class="table-name-large">{{ selectedTable.name }}</div>
            <v-chip
              :color="tableStatusColor(selectedTable.status)"
              text-color="white"
              small
            >
              {{ $t(selectedTable.status) }}
            </v-chip>
          </div>
          <v-btn icon @click="closeTableDetails">
            <v-icon>fal fa-times</v-icon>
          </v-btn>
        </div>

        <v-divider class="my-1" />

        <!-- Order Details -->
        <div v-if="order_table && order_table.code" class="order-details">
          <v-card-text class="pa-3">
            <div class="d-flex justify-space-between align-center mb-3">
              <div>
                <div class="text-h6">{{ order_table.code }}</div>
                <div class="text-caption grey--text">
                  {{ order_table.created_at | moment }}
                </div>
              </div>
              <div class="text-h5 font-weight-bold primary--text">
                {{ formatNumber(order_table.total_price) }}
              </div>
            </div>

            <!-- Action Buttons Grid -->
            <div class="action-buttons-grid">
              <v-btn
                v-for="button in mobileActionButtons"
                :key="button.key"
                small
                :color="button.color || 'primary'"
                :outlined="!button.color"
                class="action-btn"
                @click="executeAction(button.method)"
                :disabled="buttonDisabledStates[button.key]"
              >
                <v-icon left small>{{ button.icon }}</v-icon>
                {{ $t(button.label) }}
              </v-btn>
            </div>
          </v-card-text>
        </div>

        <!-- Order Items -->
        <div v-if="order_table?.history?.length > 0" class="order-items">
          <div class="items-header">
            <div class="items-title">
              {{ $t("order_items") }} ({{ order_table.history.length }})
            </div>
            <div v-if="hasSelectedItems" class="items-actions">
              <v-btn
                x-small
                color="error"
                outlined
                @click="cancelSelectedItems"
                class="mr-1"
              >
                <v-icon left x-small>fal fa-times</v-icon>
                {{ $t("cancel") }}
              </v-btn>
              <v-btn
                x-small
                color="success"
                @click="serveSelectedItems"
                class="mr-1"
              >
                <v-icon left x-small>fal fa-check</v-icon>
                {{ $t("served") }}
              </v-btn>
            </div>
          </div>

          <div class="order-items-table-container">
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="pa-2">
                      <v-checkbox
                        v-model="allSelectedState"
                        dense
                        hide-details
                      />
                    </th>
                    <th class="pa-2">{{ $t("menu_name") }}</th>
                    <th class="text-center pa-2">{{ $t("qty") }}</th>
                    <th class="text-center pa-2">{{ $t("status") }}</th>
                    <th class="text-right pa-2">{{ $t("price") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in order_table.history" :key="item.id">
                    <td class="pa-2">
                      <v-checkbox v-model="item.selected" dense hide-details />
                    </td>
                    <td class="pa-2">
                      <div class="item-name">{{ item.product_id ? item.product_id.name : $t("unknown_product") }}</div>
                      <div
                        v-if="item.product_variant?.sku"
                        class="text-caption grey--text"
                      >
                        SKU: {{ item.product_variant.sku }}
                      </div>
                    </td>
                    <td class="text-center pa-2">{{ item.qty }}</td>
                    <td class="text-center pa-2">
                      <span
                        :style="{
                          color: getComputedStatusColor(item.status),
                          fontSize: '12px',
                          fontWeight: '500',
                        }"
                      >
                        {{ $t(item.status) }}
                      </span>
                    </td>
                    <td class="text-right pa-2">
                      <span class="text-caption">
                        {{ formatNumber(item.price_sale) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>

        <!-- No Orders State -->
        <div v-else class="no-orders-state">
          <v-card-text class="text-center pa-4">
            <v-icon size="32" color="grey lighten-1"> fal fa-receipt </v-icon>
            <div class="text-body-2 grey--text mt-2">No Orders</div>
            <v-btn color="primary" class="mt-3" @click="addOrder">
              <v-icon left>fal fa-plus</v-icon>
              Add Order
            </v-btn>
          </v-card-text>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Table Context Menu -->
    <v-menu
      v-model="showContextMenu"
      :position-x="contextMenuX"
      :position-y="contextMenuY"
      absolute
      offset-y
    >
      <v-list dense>
        <v-list-item @click="viewItem(contextMenuTable)">
          <v-list-item-icon>
            <v-icon small>fal fa-eye</v-icon>
          </v-list-item-icon>
          <v-list-item-title>View</v-list-item-title>
        </v-list-item>
        <v-list-item @click="editItem(contextMenuTable)">
          <v-list-item-icon>
            <v-icon small>fal fa-edit</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Edit</v-list-item-title>
        </v-list-item>
        <v-list-item @click="deleteItem(contextMenuTable)">
          <v-list-item-icon>
            <v-icon small>fal fa-trash-alt</v-icon>
          </v-list-item-icon>
          <v-list-item-title>Delete</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Loading Overlay -->
    <v-overlay :value="overlayState" :key="overlayKey" :z-index="9999" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Dialog Components (reuse from original) -->
    <app-custom-dialog
      v-model="dialog"
      :footer="false"
      title="description"
      @submit="submitForm"
    >
      <div slot="activator" class="px-6 py-3">
        <v-row>
          <v-col cols="4">Table ID</v-col>
          <v-col cols="8">{{ form.id }}</v-col>
          <v-col cols="4">Table Name</v-col>
          <v-col cols="8">{{ form.name }}</v-col>
          <v-col cols="4">Status</v-col>
          <v-col cols="8">
            <v-chip
              label
              :color="tableStatusColor(form.status)"
              small
              class="white--text"
            >
              {{ form.status }}
            </v-chip>
          </v-col>
          <v-col cols="4">Description</v-col>
          <v-col cols="8">{{ form.note ?? "..." }}</v-col>
        </v-row>
      </div>
    </app-custom-dialog>

    <!-- QR Code Dialog -->
    <app-custom-dialog
      v-model="show_qr"
      :footer="false"
      title="qr_code_generate"
    >
      <div slot="activator">
        <v-card-text class="text-center pa-6">
          <div v-if="qr">
            <img
              :src="qr"
              alt="QR Code"
              style="max-width: 100%; height: auto"
            />
          </div>
          <div v-else class="pa-4">
            <v-progress-circular indeterminate color="primary" />
          </div>
        </v-card-text>
      </div>
    </app-custom-dialog>

    <!-- Add/Edit Table Dialog -->
    <app-custom-dialog
      v-model="dialogAdd"
      :footer="true"
      :title="update ? 'update' : 'new_data'"
      @submit="submitForm"
    >
      <div slot="activator">
        <TTextField
          v-model="form.name"
          :label="$t('table_name')"
          outlined
          dense
          class="mb-3"
        />
        <TextSelect
          v-model="form.zone_id"
          :items="translatedZoneList"
          text="name"
          itemValue="id"
          :label="$t('zone')"
          outlined
          dense
          class="mb-3"
        />
        <TextSelect
          v-if="update"
          v-model="form.status"
          :items="translatedStatusList"
          text="name"
          itemValue="id"
          :label="$t('status')"
          outlined
          dense
          class="mb-3"
        />
        <AppTextArea
          v-model="form.note"
          :label="$t('description')"
          outlined
          dense
          :rows="3"
        />
      </div>
    </app-custom-dialog>

    <!-- Payment Modal -->
    <app-custom-dialog
      v-model="paymentModal"
      :footer="false"
      :title="actionHandler"
      :width="actionHandler === 'customer' ? '650px' : '500px'"
    >
      <div slot="activator">
        <!-- back fab icon button -->
        <v-btn
          icon
          @click="actionHandler = 'payment'"
          v-if="actionHandler !== 'payment'"
        >
          <v-icon>fal fa-arrow-left</v-icon>
        </v-btn>
        <!-- payment -->
        <template v-if="actionHandler === 'payment'">
          <UnifiedPaymentPad
            :total-amount="amountDue"
            :store-type="store_type"
            :active-currencies="activeCurrencies"
            :applied-discount="appliedDiscount"
            @submit="handlePaymentSubmit"
            @paymentAmountUpdated="handlePaymentAmountUpdated"
            @manualPaymentSuccess="handleManualPaymentSuccess"
            @currency-change="onCurrencyChange"
            @customer="openCreateCustomerDialog"
            @openNoted="openNoted"
            @exchangeRate="openExchangeRate"
            @switchToCash="handleSwitchToCash"
            ref="paymentPad"
          />
        </template>
        <!-- customer -->
        <template v-if="actionHandler === 'customer'">
          <AppCustomer @selectCustomer="actionHandler = 'payment'" />
        </template>
        <!-- note -->
        <template v-if="actionHandler === 'note'">
          <app-text-area v-model="note" placeholder="noted" />
        </template>
        <!-- exchange rate -->
        <template v-if="actionHandler === 'exchangeRate'">
          <NumericExchangeRate
            :totalPrice="amountDue"
            :currencies="activeCurrencies"
            :productCurrency="actualProductCurrency"
            :debug="true"
            @submit="submitExchangeRate"
            @currency-change="onCurrencyChange"
          />
        </template>
      </div>
    </app-custom-dialog>

    <!-- Discount Modal -->
    <app-custom-dialog
      v-model="discountModal"
      title="discount_modal_title"
      width="400px"
      :footer="false"
    >
      <template slot="activator">
        <DiscountModal
          :total-before-discount="totalBeforeDiscount"
          :discount-type="appliedDiscount?.type || 'amount'"
          :initial-amount="appliedDiscount?.value?.toString() || '0'"
          :currency-symbol="'₭'"
          :show-total="true"
          submit-button-text="apply"
          @apply="handleDiscountApply"
          @close="discountModal = false"
        />
      </template>
    </app-custom-dialog>

    <!-- Move Order Dialog -->
    <app-custom-dialog
      v-model="moveOrderModal"
      :footer="true"
      title="select_target_table"
      width="100%"
      @submit="submitMoveOrder"
    >
      <div slot="activator">
        <!-- Fixed search and filters at top -->
        <div class="move-order-search-container pa-3">
          <v-row dense>
            <v-col cols="6">
              <v-select
                v-model="moveOrderStatusFilter"
                :items="translatedStatusList"
                item-text="text"
                item-value="value"
                :label="$t('filter_by_status')"
                outlined
                dense
                clearable
                hide-details
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="moveOrderZoneFilter"
                :items="translatedZoneList"
                item-text="name"
                item-value="id"
                :label="$t('filter_by_zone')"
                outlined
                dense
                clearable
                hide-details
              />
            </v-col>
            <v-col cols="12" class="mt-2">
              <v-text-field
                v-model="moveOrderSearch"
                :placeholder="$t('search_tables')"
                outlined
                dense
                clearable
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon small>fal fa-search</v-icon>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </div>
        <v-divider />
        <div v-if="loadingTablesForMove" class="text-center pa-4">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <div class="mt-2">{{ $t("loading_tables") }}</div>
        </div>
        <div v-else class="move-order-tables-container pa-2">
          <v-row dense>
            <v-col
              v-for="table in availableTablesForMove"
              :key="table.id"
              cols="6"
              sm="4"
              class="pa-1"
            >
              <v-card
                :class="[
                  'table-card-move',
                  { 'selected-table': selectedMoveTargetTable === table.id },
                ]"
                outlined
                @click="selectMoveTargetTable(table.id)"
                :elevation="selectedMoveTargetTable === table.id ? 4 : 1"
              >
                <v-card-text class="text-center pa-2">
                  <div class="text-subtitle-2 font-weight-bold mb-1">
                    {{ table.name }}
                  </div>
                  <v-chip
                    :color="tableStatusColor(table.status)"
                    x-small
                    text-color="white"
                    class="mb-1"
                  >
                    {{ $t(table.status) }}
                  </v-chip>
                  <div v-if="table.zone" class="text-caption grey--text">
                    {{ table.zone.name }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-alert v-if="availableTablesForMove.length === 0" type="info" text>
            {{
              moveOrderSearch || moveOrderStatusFilter || moveOrderZoneFilter
                ? $t("no_tables_match_filters")
                : $t("no_other_tables_found")
            }}
          </v-alert>
        </div>
      </div>
    </app-custom-dialog>

    <!-- Print Bill (Hidden) -->
    <app-bill v-show="print" :item="printBill" />
  </div>
</template>

<script>
import TTextField from "@/components/global/TextField";
import { TABLE_VALIDATE } from "@/validations/shop";
import ZONE_VALIDATE from "@/validations/zone";
import { myMixin } from "@/mixin/main";
import { tableManagementMixin } from "@/mixin/tableManagement";
import TextSelect from "@/components/global/TextSelect";
import NumericKeyboard from "@/components/NumericKeyboard";
import AppBill from "@/components/print/bill";
import AppAction from "@/components/tables/action";
import AppChooseTable from "@/components/restaurant/chooseTable";
import UnifiedPaymentPad from "@/components/payment/UnifiedPaymentPad.vue";
import draggable from "vuedraggable";
import DiscountModal from "@/components/layouts/DiscountModal.vue";
import AppCustomer from "@/components/customers/CustomerSelector.vue";
import NumericExchangeRate from "@/components/NumericExchangeRate.vue";
import AppTextArea from "@/components/global/TextArea";

export default {
  mixins: [TABLE_VALIDATE, ZONE_VALIDATE, myMixin, tableManagementMixin],
  data() {
    return {
      // Mobile-specific properties only
      pullToRefreshRefreshing: false,
      showFabMenu: false,
      fabMenuX: 0,
      fabMenuY: 0,
      showTableDetails: false,
      contextMenuTable: null,
      contextMenuX: 0,
      contextMenuY: 0,
      showContextMenu: false,
      showQr: false,
      touchStartTime: 0,
      touchStartY: 0,
      touchEndY: 0,
      mobileActionButtons: [
        {
          key: "checkout",
          label: "checkout",
          icon: "fas fa-cash-register",
          method: "checkout",
          color: "primary",
        },
        {
          key: "add_order",
          label: "add_order",
          icon: "fas fa-plus",
          method: "addOrder",
        },
        {
          key: "merge_table",
          label: "merge_table",
          icon: "fas fa-layer-group",
          method: "mergeTable",
        },
        {
          key: "move_order",
          label: "move_order",
          icon: "fas fa-exchange-alt",
          method: "moveOrder",
        },
      ],
    };
  },

  components: {
    TextSelect,
    TTextField,
    NumericKeyboard,
    AppBill,
    AppAction,
    AppChooseTable,
    UnifiedPaymentPad,
    draggable,
    DiscountModal,
    AppCustomer,
    NumericExchangeRate,
    AppTextArea,
  },
  computed: {
    // Computed property to ensure reactivity with store
    overlayState() {
      return this.$store.getters["index/overlay"];
    },
    // Key to force re-render when overlay changes
    overlayKey() {
      return `overlay-${this.overlayState ? "true" : "false"}`;
    },
  },
  methods: {
    // Override handleDiscountApply to work with new DiscountModal component
    handleDiscountApply(discountData) {
      try {
        if (!discountData) {
          toast(this, "invalid_discount_value", "error");
          return;
        }

        this.appliedDiscount = discountData;
        this.discountModal = false;
        toast(this, "discount_applied_success", "success");
      } catch (error) {
        console.error("Error applying discount:", error);
        toast(this, "error_applying_discount", "error");
      }
    },
    // Mobile-specific methods only
    closeTableDetails() {
      this.showTableDetails = false;
    },

    handleTouchStart(table, event) {
      this.touchStartTime = Date.now();
      this.touchStartY = event.touches[0].clientY;
    },

    handleTouchEnd(event) {
      const touchEndTime = Date.now();
      const touchEndY = event.changedTouches[0].clientY;

      if (touchEndTime - this.touchStartTime < 200) {
        return;
      }

      if (this.touchStartY - touchEndY > 50) {
        this.triggerPullToRefresh();
      }
    },

    triggerPullToRefresh() {
      this.pullToRefreshRefreshing = true;
      this.fetchItem().finally(() => {
        this.pullToRefreshRefreshing = false;
      });
    },

    toggleTableMenu(table, event) {
      this.contextMenuTable = table;
      this.contextMenuX = event.clientX;
      this.contextMenuY = event.clientY;
      this.showContextMenu = true;
    },

    showTableMenu(table, event) {
      this.contextMenuTable = table;
      this.contextMenuX = event.clientX;
      this.contextMenuY = event.clientY;
      this.showContextMenu = true;
    },

    formatNumber(val) {
      if (val === null || val === undefined) return "0";
      if (typeof val === "string") {
        val = parseFloat(val);
      }

      if (val % 1 === 0) {
        return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      } else {
        return val
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
    },
    navigateToZones() {
      this.$router.push({ name: "zones" });
    },
    async handleQuantityDialogSubmit() {
      if (this.$refs.leftoverQuantityDialogRef) {
        const adjustedItems =
          this.$refs.leftoverQuantityDialogRef.getAdjustedItems();
        await this.confirmLeftoverQuantities(adjustedItems);
      }
    },
  },
};
</script>

<style scoped>
/* Mobile-first responsive styles */
.tables-mobile-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Statistics Section */
.stats-section {
  padding: 2px 0;
}

.stats-scroll-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.stats-scroll-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.stats-cards {
  display: flex;
  gap: 8px;
  padding: 2px 8px;
  min-width: min-content;
}

.stat-card {
  flex-shrink: 0;
  min-width: 65px;
  height: 65px;
  border-radius: 6px;
  border: 2px solid #e0e0e0;
  padding: 6px 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.stat-card:active {
  transform: scale(0.95);
}

.stat-card-active {
  border-color: var(--primary, #5b39c9) !important;
  border-width: 2px !important;
  box-shadow: 0 2px 8px rgba(91, 57, 201, 0.2) !important;
}

.stat-card-content {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  justify-content: center;
  min-width: 85px;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: bold;
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  line-height: 1;
  margin-bottom: 6px;
}

.search-card {
  box-shadow: none;
}

/* Tables Grid Section */
.tables-grid-section {
  flex: 1;
  overflow: hidden;
}

.tables-container {
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px;
}

.tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  padding-bottom: 80px; /* Space for FAB */
}

.table-card-wrapper {
  position: relative;
}

.table-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: visible;
}

.table-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.table-card.table-selected {
  border-color: var(--v-primary-base);
  box-shadow: 0 0 0 2px var(--v-primary-lighten4);
}

.table-card.has-new-order::before {
  content: "";
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: 12px;
  z-index: -1;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.new-order-indicator {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background-color: #4caf50;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.table-name {
  font-weight: 600;
  font-size: 0.85rem;
  line-height: 1.2;
  flex: 1;
}

.table-status {
  margin-left: 6px;
}

.zone-info {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.zone-name {
  font-size: 0.75rem;
}

.order-info {
  margin-bottom: 6px;
}

.order-amount {
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.order-items {
  display: block;
  width: 100%;
}

.amount-value {
  font-weight: 600;
  color: var(--v-primary-base);
}

.quick-actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.empty-state-content {
  text-align: center;
}

/* Loading States */
.loading-state,
.infinite-scroll-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.infinite-scroll-loading {
  padding: 16px;
}

/* Floating Action Buttons */
.fab-container {
  position: fixed;
  bottom: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 100;
}

.fab-main {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Bottom Sheet */
.table-details-bottom-sheet {
  max-height: 80vh;
}

.sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
}

.sheet-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-name-large {
  font-size: 1.2rem;
  font-weight: 600;
}

.action-buttons-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 16px;
}

.action-btn {
  height: 40px !important;
}

.items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
}

.items-title {
  font-weight: 600;
  font-size: 1rem;
}

.items-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-items-table-container {
  max-height: 300px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.order-items-table-container .v-data-table {
  overflow: visible;
}

.no-orders-state {
  border-top: 1px solid #e0e0e0;
}

/* Responsive adjustments */
@media (max-width: 360px) {
  .stats-cards {
    padding: 8px 12px;
  }

  .tables-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 8px;
  }

  .action-buttons-grid {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 768px) {
  .tables-grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }

  .action-buttons-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Dark theme support - removed custom background colors to use theme */

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Move Order Dialog Styles */
.move-order-search-container {
  background: #f5f5f5;
}

.move-order-tables-container {
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.table-card-move {
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 80px;
}

.table-card-move:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Print styles */
@media print {
  .fab-container,
  .sheet-header,
  .action-buttons-grid,
  .items-actions {
    display: none !important;
  }

  .table-details-bottom-sheet {
    max-height: none;
  }
}
</style>
